import { ChevronDown, ChevronUp, RefreshCcw, Search, User } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'

export default function ReassignSalesDropdown({
  sales = [],
  currentSalesPersonId,
  onReassign,
}) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isReassigning, setIsReassigning] = useState(false)
  const reassigningRef = useRef(false)

  const availableSales = useMemo(
    () => sales.filter((person) => person._id !== currentSalesPersonId),
    [sales, currentSalesPersonId],
  )

  const filteredSales = useMemo(() => {
    if (!searchQuery.trim()) return availableSales
    const q = searchQuery.toLowerCase().trim()
    return availableSales.filter((person) =>
      person.name?.toLowerCase().includes(q),
    )
  }, [availableSales, searchQuery])

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setSearchQuery('')
    }
    setOpen(isOpen)
  }

  const handleReassign = async (person) => {
    if (reassigningRef.current) return

    reassigningRef.current = true
    setIsReassigning(true)
    setOpen(false)

    try {
      await onReassign?.(person)
    } catch (error) {
      console.error(error)
    } finally {
      reassigningRef.current = false
      setIsReassigning(false)
    }
  }

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          disabled={isReassigning}
          className="h-8 gap-1 rounded-full bg-amber-100 px-2.5 text-[11px] font-medium text-amber-800 hover:bg-amber-200 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
        >
          <RefreshCcw
            className={`size-3.5 ${isReassigning ? 'animate-spin' : ''}`}
          />
          {isReassigning ? 'Reassigning...' : 'Reassign'}
          {!isReassigning &&
            (open ? (
              <ChevronUp className="size-3" />
            ) : (
              <ChevronDown className="size-3" />
            ))}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="bottom"
        align="end"
        sideOffset={6}
        collisionPadding={16}
        className="w-64 max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200/90 bg-white p-2 shadow-xl"
      >
        <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-slate-100 mb-1">
          <span className="text-xs font-bold text-slate-800">
            Reassign lead to
          </span>
          <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
            {filteredSales.length}
          </span>
        </div>

        {availableSales.length > 4 && (
          <div className="px-1 pb-1.5 pt-0.5">
            <div className="relative flex items-center">
              <Search className="absolute left-2.5 size-3.5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search salesperson..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/80 py-1.5 pl-8 pr-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-amber-500 focus:bg-white focus:ring-2 focus:ring-amber-100"
              />
            </div>
          </div>
        )}

        <div className="max-h-56 overflow-y-auto overscroll-contain pr-0.5 space-y-0.5 [scrollbar-width:thin]">
          {filteredSales.length > 0 ? (
            filteredSales.map((person) => (
              <DropdownMenuItem
                key={person._id}
                disabled={isReassigning}
                onSelect={() => handleReassign(person)}
                className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-amber-50 hover:text-amber-900 cursor-pointer transition-colors"
              >
                <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-800">
                  {person.name?.[0]?.toUpperCase() || (
                    <User className="size-3" />
                  )}
                </div>
                <span className="truncate">{person.name}</span>
              </DropdownMenuItem>
            ))
          ) : (
            <div className="px-3 py-4 text-center text-xs text-slate-400 font-normal">
              {searchQuery
                ? 'No matching salespersons'
                : 'No salespersons available'}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
